import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, X, FileText, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, mediaSrc } from "@/lib/api";

export function MediaThumb({ item, onClick, selected, onDelete }) {
  const isPdf = item.kind === "pdf";
  return (
    <div className={`relative group border ${selected ? "border-[#FF6A00]" : "border-[#1F2A22]/15"} bg-white`} data-testid={`media-item-${item.id}`}>
      <button type="button" onClick={onClick} className="block w-full text-left">
        <div className="h-28 bg-[#F2F0EB] grid place-items-center overflow-hidden">
          {isPdf ? <FileText size={34} className="text-[#8A5A44]" /> : <img src={mediaSrc(item.url)} alt={item.title} className="w-full h-full object-cover" />}
        </div>
        <p className="text-[11px] text-[#1F2A22] truncate px-2 py-1.5">{item.title}</p>
      </button>
      {selected && <span className="absolute top-1 left-1 bg-[#FF6A00] text-white rounded-full p-0.5"><Check size={12} /></span>}
      {onDelete && (
        <button type="button" onClick={() => onDelete(item)} className="absolute top-1 right-1 bg-white/90 hover:bg-red-50 text-red-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Delete" data-testid={`media-delete-${item.id}`}><Trash2 size={13} /></button>
      )}
    </div>
  );
}

export function useMediaLibrary(kind) {
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = () => api.mediaList(kind).then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);
  const upload = async (file) => {
    if (!file) return null;
    setUploading(true);
    try {
      const rec = await api.mediaUpload(file, file.name);
      await load();
      toast.success("Uploaded to library");
      return rec;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
      return null;
    } finally { setUploading(false); }
  };
  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}" from the library?`)) return;
    try { await api.mediaDelete(item.id); await load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };
  return { items, uploading, load, upload, remove };
}

// Modal picker used across the CMS. kind: "image" | "pdf" | undefined (all)
export default function MediaPicker({ open, onClose, onSelect, kind }) {
  const { items, uploading, upload } = useMediaLibrary(kind);
  const fileRef = useRef(null);
  if (!open) return null;
  const accept = kind === "pdf" ? "application/pdf" : kind === "image" ? "image/*" : "image/*,application/pdf";
  return (
    <div className="fixed inset-0 z-[70] bg-black/45 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-[#F9F8F6] w-full max-w-3xl max-h-[85vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="media-picker">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-[#1F2A22]">Media Library</h2>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept={accept} className="hidden" data-testid="media-picker-file"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 bg-[#FF6A00] text-white px-3 py-2 text-sm font-semibold" data-testid="media-picker-upload">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
            </button>
            <button onClick={onClose} className="p-2" aria-label="Close"><X size={20} /></button>
          </div>
        </div>
        {items === null ? (
          <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#FF6A00]" /></div>
        ) : items.length === 0 ? (
          <p className="text-[#5C6B61] py-10 text-center">Library is empty. Upload an image{kind !== "image" ? " or PDF" : ""} to get started.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="media-picker-grid">
            {items.map((it) => (
              <MediaThumb key={it.id} item={it} onClick={() => { onSelect(it.url, it); onClose(); }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
